import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { triggerTabActivityAlert } from './AudioAlerts/tabActivityAlert';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.viewer_on': this.onViewerOn,
      'conversation.viewer_off': this.onViewerOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'task.completed': this.onTaskCompleted,
      'deal.updated': this.onDealUpdated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  // Tiempo real (Victor 2026-06-07): si la conversación YA está abierta o YA está
  // en la lista cargada, el evento pasa aunque sea de otra sucursal/personal. Así
  // lo que el agente está viendo se actualiza en vivo (mensaje nuevo → salta
  // arriba en Todos/Mi Chat/Urgentes) sin recargar. NO mete conversaciones NUEVAS
  // de otra sucursal: esas no están "conocidas" y siguen bloqueadas por el filtro
  // de abajo + el doble candado del action updateConversation (alreadyInList).
  isKnownConversation = data => {
    const id = data?.id ?? data?.conversation?.id ?? data?.conversation_id;
    if (id == null) return false;
    const openChat = this.app.$store.getters.getSelectedChat;
    if (openChat && Number(openChat.id) === Number(id)) return true;
    const all = this.app.$store.getters.getAllConversations || [];
    return all.some(c => Number(c.id) === Number(id));
  };

  // Multi-sucursal: filtra eventos cuya conversación NO pertenece a la branch activa.
  // Si el payload llega sin branch_id (caso anómalo) → NO pasa (anti-leak estricto).
  // Backend Conversations::EventDataPresenter ahora incluye branch_id en push_data.
  belongsToActiveBranch = data => {
    // Lo que ya estás viendo/abriste se actualiza en vivo aunque sea de otra sucursal.
    if (this.isKnownConversation(data)) return true;
    const branches = this.app.$store.getters['branches/getBranches'] || [];
    let activeBranchId = this.app.$store.getters['branches/getActiveBranchId'];
    // Respaldo durante la carga inicial (incógnito / primer login / caché limpio):
    // antes de que se resuelva la sucursal activa, usa la sucursal principal del
    // usuario para no dejar pasar eventos de otra sucursal en ese segundo de carga.
    if (!activeBranchId) {
      activeBranchId =
        this.app.$store.getters.getCurrentUser?.primary_branch_id || null;
    }
    if (!activeBranchId) {
      // Sin sucursal resuelta: solo procesar si la cuenta NO usa sucursales.
      // Si ya hay sucursales cargadas, bloquear (anti-leak estricto en la carga).
      return branches.length === 0;
    }
    const branchId = data?.conversation?.branch_id ?? data?.branch_id;
    if (branchId == null) return false; // estricto · backend siempre debería incluir branch_id
    if (Number(branchId) === Number(activeBranchId)) return true;
    // Vista compartida (Victor 2026-05-24): las sucursales "conectadas" (SDO+SDE)
    // comparten pool — la lista muestra conversaciones de TODAS las conectadas.
    // El websocket debe aceptar eventos cross-branch entre conectadas, si no la
    // tarjeta de la sucursal hermana NO se actualiza (ej. al resolver se queda
    // abierta para quien está parado en la otra sucursal del pool). Espejo de
    // shared_branch_scope? del backend (conversation_finder.rb).
    const isConnected = id =>
      branches.some(b => Number(b.id) === Number(id) && b.connected);
    return isConnected(activeBranchId) && isConnected(branchId);
  };

  onMessageUpdated = data => {
    if (!this.belongsToActiveBranch(data)) return;
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    if (!this.belongsToActiveBranch(payload)) return;
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    if (!this.belongsToActiveBranch(data)) return;
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    if (!this.belongsToActiveBranch(data)) return;
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    if (!this.belongsToActiveBranch(data)) return;
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
    // Refresca el preview "el cliente escribió" en el Kanban de Leads en vivo.
    // conversationId aquí es el display_id (coincide con deal.conversation_display_id).
    emitter.emit(BUS_EVENTS.NEW_MESSAGE_FOR_DEAL, {
      conversationId,
      content: data.content,
      messageType: data.message_type,
      createdAt: data.created_at,
    });

    // Aviso en la pestaña del navegador (Propuesta 3 · Victor 2026-06-04): cada
    // pestaña es INDEPENDIENTE — solo avisa si escribe el cliente cuyo chat ESTA
    // pestaña tiene abierto. Si tienes 4 pestañas (Juan/Pedro/María/Ana) y Ana
    // escribe, solo la pestaña de Ana parpadea "💬 Ana escribió" ⇄ "Ana · CRM".
    // (Cada pestaña del navegador tiene su propio getSelectedChat.)
    const openChat = this.app.$store.getters.getSelectedChat;
    const isOpenInThisTab = openChat && openChat.id === conversationId;
    if (
      isOpenInThisTab &&
      data.message_type === MESSAGE_TYPE.INCOMING &&
      !data.private
    ) {
      triggerTabActivityAlert({
        name: data.sender?.name || data.conversation?.meta?.sender?.name,
      });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    if (!this.belongsToActiveBranch(data)) return;
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    if (!this.belongsToActiveBranch(data)) return;
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onViewerOn = ({ conversation_id: conversationId, user, joined_at: joinedAt }) => {
    if (!conversationId || !user) return;
    this.app.$store.dispatch('conversationViewers/add', {
      conversationId,
      user,
      joinedAt,
    });
  };

  onViewerOff = ({ conversation_id: conversationId, user }) => {
    if (!conversationId || !user) return;
    this.app.$store.dispatch('conversationViewers/remove', { conversationId, userId: user.id });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
    // Aviso centrado "¡TIENES UNA NUEVA TAREA!" al asignar (Victor 2026-06-04):
    // el notification_type viene DENTRO de data.notification (no en la raíz), por
    // eso no salía. Emitimos la notificación plana para que el modal lea sus
    // campos (primary_actor, etc.).
    if (data?.notification?.notification_type === 'task_assignment') {
      emitter.emit(BUS_EVENTS.TASK_ASSIGNED_LIVE, data.notification);
    }
  };

  // Tarea completada por el agente (por confirmar): dispara el banner central
  // + campana verde para que quien la asignó la confirme (Victor 2026-06-04).
  onTaskCompleted = data => {
    emitter.emit(BUS_EVENTS.TASK_COMPLETED_LIVE, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onDealUpdated = data => {
    emitter.emit(BUS_EVENTS.DEAL_UPDATED_LIVE, data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
